/**
 * Inventory Cards — Client-Side Module
 *
 * Fetches live block/bundle/slab data from the FindMyMarble API and renders
 * the inventory cards UI.  All interaction handlers (accordion, galleries,
 * modals, selection state, quote submission) are wired up after render.
 *
 * Dependencies:
 *   - window.FindMyMarbleAPI  (assets/findmymarble-api.js)
 *   - section-inventory-cards.css
 *   - inventory-quote-request-form.liquid  (rendered by Liquid shell)
 *
 * This file is loaded with defer from the inventory-cards.liquid section
 * and only runs when demo_mode is false.
 */
(function () {
  'use strict';

  /* ========================================================================
   * CONSTANTS
   * ====================================================================== */

  var SELECTORS = {
    section:  '[data-inventory-section]',
    loading:  '[data-loading]',
    content:  '[data-content]',
    error:    '[data-error]',
    empty:    '[data-empty]',
    retry:    '[data-retry]'
  };

  var MESSAGES = {
    apiMissing: 'Inventory data is not available at this time.',
    loadError:  'Unable to load inventory data.',
    empty:      'No inventory currently available for this stone type.'
  };

  /* ========================================================================
   * HELPERS
   * ====================================================================== */

  /**
   * HTML-escape a string to prevent XSS when building innerHTML.
   */
  function esc(str) {
    if (!str) return '';
    var div = document.createElement('div');
    div.appendChild(document.createTextNode(String(str)));
    return div.innerHTML;
  }

  /**
   * Convert millimetres to centimetres for display.
   */
  function mmToCm(mm) {
    if (mm == null) return '';
    var cm = (parseFloat(mm) / 10);
    return cm.toFixed(1).replace(/\.0$/, '');
  }

  /**
   * Format an area value (m2) to 1 decimal place.
   */
  function fmtArea(val) {
    if (val == null) return '0';
    var n = parseFloat(val);
    if (isNaN(n)) return '0';
    return n.toFixed(1).replace(/\.0$/, '0.0').replace(/^0\.0$/, '0');
  }

  /**
   * Format area to 1dp for display — simpler version.
   */
  function fmtArea1(val) {
    var n = parseFloat(val);
    if (isNaN(n)) return '0';
    return n.toFixed(1);
  }

  /**
   * Format area to 2dp for detail display.
   */
  function fmtArea2(val) {
    var n = parseFloat(val);
    if (isNaN(n)) return '0.00';
    return n.toFixed(2);
  }

  /**
   * Capitalize first letter.
   */
  function capitalize(str) {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  /**
   * Poll for window.FindMyMarbleAPI, resolve when available.
   * Rejects after maxWait ms.
   */
  function waitForAPI(maxWait) {
    return new Promise(function (resolve, reject) {
      if (window.FindMyMarbleAPI) {
        resolve(window.FindMyMarbleAPI);
        return;
      }
      var elapsed = 0;
      var interval = 100;
      var timer = setInterval(function () {
        elapsed += interval;
        if (window.FindMyMarbleAPI) {
          clearInterval(timer);
          resolve(window.FindMyMarbleAPI);
        } else if (elapsed >= maxWait) {
          clearInterval(timer);
          reject(new Error(MESSAGES.apiMissing));
        }
      }, interval);
    });
  }

  /**
   * Map project_type form value to API customer_type enum.
   */
  function mapProjectType(projectType) {
    if (!projectType) return undefined;
    var lower = projectType.toLowerCase();
    if (lower.indexOf('residential') !== -1) return 'fabricator';
    if (lower.indexOf('commercial') !== -1) return 'contractor';
    if (lower.indexOf('other') !== -1) return 'architect_designer';
    return 'fabricator';
  }

  /* ========================================================================
   * FIELD MAPPING — API response shapes to UI data
   * ====================================================================== */

  function mapBlock(apiBlock) {
    return {
      id:           apiBlock.id,
      block_code:   apiBlock.block_code || '',
      stone_name:   apiBlock.stone_name || '',
      available_sqm: parseFloat(apiBlock.available_sqm) || 0,
      avg_length_cm: mmToCm(apiBlock.average_length_mm),
      avg_width_cm:  mmToCm(apiBlock.average_width_mm),
      thickness_cm:  mmToCm(apiBlock.thickness_mm),
      images:        apiBlock.images || [],
      videos:        apiBlock.videos || [],
      close_up_images: apiBlock.close_up_images || [],
      book_matched_images: apiBlock.book_matched_images || [],
      origin:        apiBlock.origin_country || '',
      quarry:        apiBlock.quarry_name || '',
      bundles:       []  // filled later from block detail
    };
  }

  function mapBundle(apiBundle, blockCode, blockId) {
    var images = apiBundle.images || [];
    var videos = apiBundle.videos || [];
    return {
      id:             apiBundle.id,
      block_code:     blockCode || '',
      block_id:       blockId || '',
      bundle_number:  apiBundle.bundle_number,
      bundle_no:      'Bundle ' + apiBundle.bundle_number,
      finish:         apiBundle.finish || '',
      slab_count:     apiBundle.slab_count || 0,
      available_sqm:  parseFloat(apiBundle.available_sqm) || 0,
      is_bookmatched: apiBundle.is_bookmatched || false,
      average_slab_sqm: parseFloat(apiBundle.average_slab_sqm) || 0,
      bundle_image:   images.length > 0 ? images[0] : '',
      images:         images,
      videos:         videos,
      media_assets:   mergeMediaAssets(images, videos)
    };
  }

  function mapSlab(apiSlab, finish, blockCode, blockId) {
    var images = apiSlab.images || [];
    return {
      id:           apiSlab.id,
      slab_number:  apiSlab.slab_number,
      slab_no:      String(apiSlab.slab_number),
      length_cm:    mmToCm(apiSlab.length_mm),
      width_cm:     mmToCm(apiSlab.width_mm),
      thickness_cm: mmToCm(apiSlab.thickness_mm),
      m2:           parseFloat(apiSlab.area_sqm) || 0,
      status:       apiSlab.status || 'available',
      finish:       finish || '',
      block_code:   blockCode || '',
      block_id:     blockId || '',
      images:       images,
      media_assets: images  // slab images are already all images
    };
  }

  function mergeMediaAssets(images, videos) {
    var assets = [];
    (images || []).forEach(function (url) {
      assets.push({ url: url, type: 'image' });
    });
    (videos || []).forEach(function (url) {
      assets.push({ url: url, type: 'video' });
    });
    return assets;
  }

  /* ========================================================================
   * SVG ICONS (copied verbatim from Liquid snippets)
   * ====================================================================== */

  var ICONS = {
    slabs: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">' +
      '<rect x="3" y="3" width="7" height="7"></rect>' +
      '<rect x="14" y="3" width="7" height="7"></rect>' +
      '<rect x="14" y="14" width="7" height="7"></rect>' +
      '<rect x="3" y="14" width="7" height="7"></rect>' +
      '</svg>',
    area: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">' +
      '<path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>' +
      '</svg>',
    chevronRight: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">' +
      '<polyline points="9 18 15 12 9 6"></polyline>' +
      '</svg>',
    chevronDown: '<svg class="inventory-block__chevron" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">' +
      '<polyline points="6 9 12 15 18 9"></polyline>' +
      '</svg>',
    close: '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">' +
      '<line x1="18" y1="6" x2="6" y2="18"></line>' +
      '<line x1="6" y1="6" x2="18" y2="18"></line>' +
      '</svg>',
    prevArrow: '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">' +
      '<polyline points="15 18 9 12 15 6"></polyline>' +
      '</svg>',
    nextArrow: '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">' +
      '<polyline points="9 6 15 12 9 18"></polyline>' +
      '</svg>',
    prevArrowSmall: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">' +
      '<polyline points="15 18 9 12 15 6"></polyline>' +
      '</svg>',
    nextArrowSmall: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">' +
      '<polyline points="9 6 15 12 9 18"></polyline>' +
      '</svg>',
    placeholder: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">' +
      '<rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>' +
      '<circle cx="8.5" cy="8.5" r="1.5"/>' +
      '<polyline points="21 15 16 10 5 21"/>' +
      '</svg>',
    check: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">' +
      '<polyline points="20 6 9 17 4 12"></polyline>' +
      '</svg>',
    clipboard: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">' +
      '<path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2"></path>' +
      '<rect x="9" y="3" width="6" height="4" rx="1"></rect>' +
      '<line x1="12" y1="11" x2="12" y2="17"></line>' +
      '<line x1="9" y1="14" x2="15" y2="14"></line>' +
      '</svg>',
    galleryLabel: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">' +
      '<rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>' +
      '<circle cx="8.5" cy="8.5" r="1.5"/>' +
      '<polyline points="21 15 16 10 5 21"/>' +
      '</svg>',
    playBtn: '<svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor">' +
      '<polygon points="5 3 19 12 5 21 5 3"></polygon>' +
      '</svg>',
    playBtnSmall: '<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">' +
      '<polygon points="5 3 19 12 5 21 5 3"></polygon>' +
      '</svg>'
  };

  /* ========================================================================
   * RENDER FUNCTIONS
   * ====================================================================== */

  /**
   * Summary bar showing total blocks/bundles/m2.
   */
  function renderSummaryBar(blocks) {
    var totalBundles = 0;
    var totalM2 = 0;
    blocks.forEach(function (b) {
      totalBundles += (b.bundles && b.bundles.length) || 0;
      totalM2 += b.available_sqm || 0;
    });

    return '<div class="inventory-cards__total-summary">' +
      '<span class="inventory-cards__stat"><strong>' + blocks.length + '</strong> block' + (blocks.length !== 1 ? 's' : '') + ' available</span>' +
      '<span class="inventory-cards__stat-divider"></span>' +
      '<span class="inventory-cards__stat"><strong>' + totalBundles + '</strong> bundle' + (totalBundles !== 1 ? 's' : '') + ' total</span>' +
      '<span class="inventory-cards__stat-divider"></span>' +
      '<span class="inventory-cards__stat"><strong>' + fmtArea1(totalM2) + '</strong> m&sup2; total</span>' +
      '</div>';
  }

  /**
   * Block accordion wrapper.  The content area starts empty and gets
   * populated when block detail finishes loading.
   */
  function renderBlockAccordion(block, index, settings) {
    var bundleCount = (block.bundles && block.bundles.length) || 0;
    var isFirst = index === 0;

    var originHtml = block.origin
      ? '<span class="inventory-block__origin">' + esc(block.origin) + '</span>'
      : '';

    var statsHtml = '<span class="inventory-block__stats">' +
      bundleCount + ' bundle' + (bundleCount !== 1 ? 's' : '') +
      ' &bull; ' + fmtArea1(block.available_sqm) + ' m&sup2;</span>';

    return '<div class="inventory-block" data-block-accordion data-block-id="' + esc(block.id) + '">' +
      '<button type="button" class="inventory-block__header" data-block-toggle aria-expanded="true">' +
        '<div class="inventory-block__header-content">' +
          '<h3 class="inventory-block__title">Block ' + esc(block.block_code) + '</h3>' +
          '<div class="inventory-block__meta">' +
            originHtml +
            statsHtml +
          '</div>' +
        '</div>' +
        ICONS.chevronDown +
      '</button>' +
      '<div class="inventory-block__content" data-block-content>' +
        '<div class="inventory-cards__grid inventory-cards__grid--' + esc(settings.columns) + '"' +
          ' data-block-grid="' + esc(block.id) + '">' +
          renderBlockGallery(block) +
          '<div data-bundle-placeholder="' + esc(block.id) + '" class="inventory-cards__loading-bundles">' +
            '<div class="inventory-cards__skeleton-card"></div>' +
            '<div class="inventory-cards__skeleton-card"></div>' +
          '</div>' +
        '</div>' +
      '</div>' +
    '</div>';
  }

  /**
   * Block gallery card — large gallery at the start of each block row.
   * Matches snippets/inventory-block-gallery.liquid exactly.
   */
  function renderBlockGallery(block) {
    var images = block.images || [];
    var videos = block.videos || [];
    var hasImages = images.length > 0;
    var hasVideo = videos.length > 0;
    var hasMedia = hasImages || hasVideo;
    var imageCount = images.length;

    var html = '<div class="block-gallery-card' + (!hasMedia ? ' block-gallery-card--placeholder' : '') + '" data-block-gallery-card>';
    html += '<div class="block-gallery-card__media">';

    if (hasImages) {
      html += '<div class="block-gallery-card__gallery" data-block-card-gallery>';
      html += '<div class="block-gallery-card__gallery-main">';
      images.forEach(function (url, i) {
        html += '<img' +
          ' src="' + esc(url) + '"' +
          ' alt="' + esc(block.block_code) + '"' +
          ' loading="' + (i === 0 ? 'eager' : 'lazy') + '"' +
          ' class="block-gallery-card__image' + (i === 0 ? ' is-active' : '') + '"' +
          ' data-block-card-image="' + i + '">';
      });
      html += '</div>';

      if (imageCount > 1) {
        html += '<button type="button" class="block-gallery-card__nav block-gallery-card__nav--prev" data-block-card-prev aria-label="Previous image">' + ICONS.prevArrow + '</button>';
        html += '<button type="button" class="block-gallery-card__nav block-gallery-card__nav--next" data-block-card-next aria-label="Next image">' + ICONS.nextArrow + '</button>';
        html += '<div class="block-gallery-card__counter"><span data-block-card-current>1</span> / ' + imageCount + '</div>';
      }

      html += '</div>';
    } else if (hasVideo) {
      html += '<div class="block-gallery-card__video-wrapper">';
      html += '<video src="' + esc(videos[0]) + '" controls playsinline preload="metadata" class="block-gallery-card__video">Your browser does not support the video tag.</video>';
      html += '<div class="block-gallery-card__video-overlay" data-block-video-overlay>';
      html += '<button type="button" class="block-gallery-card__video-play" data-block-video-play aria-label="Play video">' + ICONS.playBtn + '</button>';
      html += '</div>';
      html += '</div>';
    } else {
      html += '<div class="block-gallery-card__placeholder">' + ICONS.placeholder + '</div>';
    }

    html += '</div>'; // .block-gallery-card__media

    if (hasMedia) {
      html += '<div class="block-gallery-card__content">';
      html += '<div class="block-gallery-card__header">';
      html += '<h4 class="block-gallery-card__title">' + esc(block.block_code) + '</h4>';
      if (block.origin) {
        html += '<span class="block-gallery-card__origin">' + esc(block.origin) + '</span>';
      }
      html += '</div>';
      html += '<div class="block-gallery-card__label">' + ICONS.galleryLabel + '<span>Block Gallery</span></div>';

      if (hasVideo && hasImages) {
        html += '<button type="button" class="block-gallery-card__video-btn" data-open-block-video aria-label="Watch video">' +
          ICONS.playBtnSmall + 'Watch Video</button>';
      }

      html += '</div>';
    }

    html += '</div>';
    return html;
  }

  /**
   * Bundle card for grid view.
   * Matches snippets/inventory-bundle-card.liquid exactly.
   */
  function renderBundleCard(bundle, settings, modalId) {
    var imageRatio = settings.imageRatio || 'landscape';

    var imageHtml;
    if (bundle.bundle_image) {
      imageHtml = '<img src="' + esc(bundle.bundle_image) + '"' +
        ' alt="' + esc(bundle.bundle_no) + '"' +
        ' loading="lazy" class="bundle-card__image">';
    } else {
      imageHtml = '<div class="bundle-card__placeholder">' + ICONS.placeholder + '</div>';
    }

    var finishHtml = '';
    if (bundle.finish) {
      finishHtml = '<span class="bundle-card__meta-item">' + esc(capitalize(bundle.finish)) + '</span>';
    }

    return '<div class="bundle-card">' +
      '<div class="bundle-card__image-wrapper bundle-card__image-wrapper--' + esc(imageRatio) + '">' +
        imageHtml +
      '</div>' +
      '<div class="bundle-card__content">' +
        '<h4 class="bundle-card__title">' + esc(bundle.bundle_no) + '</h4>' +
        '<div class="bundle-card__meta">' +
          '<span class="bundle-card__meta-item">' + ICONS.slabs +
            bundle.slab_count + ' slab' + (bundle.slab_count !== 1 ? 's' : '') +
          '</span>' +
          '<span class="bundle-card__meta-item">' + ICONS.area +
            fmtArea1(bundle.available_sqm) + ' m&sup2;' +
          '</span>' +
          finishHtml +
        '</div>' +
        '<button type="button" class="bundle-card__button btn btn--secondary"' +
          ' data-open-bundle-modal="' + esc(modalId) + '"' +
          ' aria-haspopup="dialog">' +
          'View Slabs ' + ICONS.chevronRight +
        '</button>' +
      '</div>' +
    '</div>';
  }

  /**
   * Bundle modal — opened when clicking "View Slabs".
   * Matches snippets/inventory-bundle-modal.liquid exactly.
   * The slab grid is initially empty (lazy loaded).
   */
  function renderBundleModal(bundle, block, settings, modalId) {
    var imageAssets = bundle.media_assets.filter(function (a) { return a.type === 'image'; });
    var imageCount = imageAssets.length;

    var html = '<div class="bundle-modal" id="' + esc(modalId) + '" role="dialog" aria-modal="true" aria-labelledby="' + esc(modalId) + '-title">';
    html += '<div class="bundle-modal__container">';

    // Header
    html += '<div class="bundle-modal__header">';
    html += '<h3 class="bundle-modal__title" id="' + esc(modalId) + '-title">' + esc(bundle.bundle_no) + '</h3>';
    html += '<button type="button" class="bundle-modal__close" data-close-bundle-modal aria-label="Close modal">' + ICONS.close + '</button>';
    html += '</div>';

    // Body
    html += '<div class="bundle-modal__body">';

    // Gallery
    if (imageCount > 0) {
      html += '<div class="bundle-modal__hero bundle-modal__gallery" data-gallery>';
      html += '<div class="bundle-modal__gallery-main">';
      imageAssets.forEach(function (asset, i) {
        html += '<img src="' + esc(asset.url) + '"' +
          ' alt="' + esc(bundle.bundle_no) + '"' +
          ' loading="' + (i === 0 ? 'eager' : 'lazy') + '"' +
          ' class="bundle-modal__hero-image' + (i === 0 ? ' is-active' : '') + '"' +
          ' data-gallery-image="' + i + '">';
      });
      html += '</div>';

      if (imageCount > 1) {
        html += '<button type="button" class="bundle-modal__gallery-nav bundle-modal__gallery-nav--prev" data-gallery-prev aria-label="Previous image">' + ICONS.prevArrow + '</button>';
        html += '<button type="button" class="bundle-modal__gallery-nav bundle-modal__gallery-nav--next" data-gallery-next aria-label="Next image">' + ICONS.nextArrow + '</button>';

        // Thumbnails
        html += '<div class="bundle-modal__gallery-thumbs">';
        imageAssets.forEach(function (asset, i) {
          html += '<button type="button" class="bundle-modal__gallery-thumb' + (i === 0 ? ' is-active' : '') + '"' +
            ' data-gallery-thumb="' + i + '" aria-label="View image ' + (i + 1) + '">' +
            '<img src="' + esc(asset.url) + '" alt="" loading="lazy">' +
          '</button>';
        });
        html += '</div>';

        // Counter
        html += '<div class="bundle-modal__gallery-counter"><span data-gallery-current>1</span> / ' + imageCount + '</div>';
      }

      html += '</div>';
    } else if (bundle.bundle_image) {
      html += '<div class="bundle-modal__hero">';
      html += '<img src="' + esc(bundle.bundle_image) + '" alt="' + esc(bundle.bundle_no) + '" loading="lazy" class="bundle-modal__hero-image">';
      html += '</div>';
    }

    // Bundle info
    html += '<div class="bundle-modal__info"><div class="bundle-modal__meta">';
    html += '<span>' + bundle.slab_count + ' slab' + (bundle.slab_count !== 1 ? 's' : '') + ' available</span>';
    html += '<span>' + fmtArea1(bundle.available_sqm) + ' m&sup2; total</span>';
    html += '</div></div>';

    // Select entire bundle (shown when show_bundle_buy is true and bundle has slabs)
    if (settings.showBundleBuy && bundle.slab_count > 0) {
      html += '<div class="bundle-modal__buy-bundle">';
      html += '<label class="bundle-modal__buy-bundle-select">';
      html += '<input type="checkbox" class="bundle-modal__bundle-checkbox"' +
        ' data-select-bundle' +
        ' data-bundle-id="' + esc(bundle.id) + '"' +
        ' data-bundle-code="' + esc(bundle.bundle_no) + '"' +
        ' data-bundle-product="' + esc(block.stone_name) + '"' +
        ' data-bundle-block="' + esc(block.block_code) + '"' +
        ' data-bundle-block-id="' + esc(block.id) + '"' +
        ' data-bundle-slab-count="' + bundle.slab_count + '"' +
        ' data-bundle-total-m2="' + fmtArea1(bundle.available_sqm) + '"' +
        ' data-bundle-finish="' + esc(bundle.finish) + '"' +
        ' data-bundle-price="0">';
      html += '<span class="bundle-modal__bundle-checkbox-custom">' + ICONS.check + '</span>';
      html += '</label>';
      html += '<div class="bundle-modal__buy-bundle-content">';
      html += '<div class="bundle-modal__buy-bundle-text">';
      html += '<strong>Select Entire Bundle</strong>';
      html += '<span>Get all ' + bundle.slab_count + ' matching slabs for consistent veining</span>';
      html += '</div>';
      html += '</div>';
      html += '</div>';

      html += '<div class="bundle-modal__divider"><span>Or select individual slabs</span></div>';
    } else {
      html += '<div class="bundle-modal__divider"><span>Select individual slabs</span></div>';
    }

    // Slabs container (lazy loaded)
    html += '<div class="bundle-modal__slabs" data-modal-slabs="' + esc(bundle.id) + '">';
    html += '<div class="inventory-cards__loading-slabs">';
    html += '<div class="inventory-cards__skeleton-card"></div>';
    html += '<div class="inventory-cards__skeleton-card"></div>';
    html += '<div class="inventory-cards__skeleton-card"></div>';
    html += '</div>';
    html += '</div>';

    html += '</div>'; // .bundle-modal__body
    html += '</div>'; // .bundle-modal__container

    // Footer
    html += '<div class="bundle-modal__footer" data-modal-footer>';
    html += '<div class="bundle-modal__footer-summary">';
    html += '<span class="bundle-modal__footer-count"><strong data-modal-selected-count>0</strong> selected</span>';
    html += '<span class="bundle-modal__footer-divider"></span>';
    html += '<span class="bundle-modal__footer-m2"><strong data-modal-selected-m2>0</strong> m&sup2;</span>';
    html += '</div>';
    html += '<button type="button" class="bundle-modal__footer-button btn btn--primary" data-modal-add-to-quote>' +
      ICONS.clipboard + ' Add to Quote</button>';
    html += '</div>';

    html += '</div>';
    return html;
  }

  /**
   * Slab card — rendered inside the bundle modal.
   * Matches snippets/inventory-bundle-modal.liquid slab markup exactly.
   */
  function renderSlabCard(slab, bundle, block, settings) {
    var imageRatio = settings.imageRatio || 'landscape';
    var images = slab.images || [];
    var imageCount = images.length;
    var hasGallery = imageCount > 1;

    var html = '<div class="slab-card" data-slab-selectable>';

    // Selection checkbox
    html += '<label class="slab-card__select">';
    html += '<input type="checkbox" class="slab-card__checkbox"' +
      ' data-select-slab' +
      ' data-slab-id="' + esc(slab.id) + '"' +
      ' data-slab-code="' + esc(slab.slab_no) + '"' +
      ' data-slab-product="' + esc(block.stone_name) + '"' +
      ' data-slab-block="' + esc(block.block_code) + '"' +
      ' data-slab-block-id="' + esc(block.id) + '"' +
      ' data-slab-length="' + esc(slab.length_cm) + '"' +
      ' data-slab-width="' + esc(slab.width_cm) + '"' +
      ' data-slab-thickness="' + esc(slab.thickness_cm) + '"' +
      ' data-slab-m2="' + fmtArea2(slab.m2) + '"' +
      ' data-slab-finish="' + esc(slab.finish || bundle.finish) + '"' +
      ' data-slab-price="0">';
    html += '<span class="slab-card__checkbox-custom">' + ICONS.check + '</span>';
    html += '</label>';

    // Image wrapper
    html += '<div class="slab-card__image-wrapper slab-card__image-wrapper--' + esc(imageRatio) +
      (hasGallery ? ' slab-card__image-wrapper--gallery' : '') + '"' +
      (hasGallery ? ' data-slab-gallery' : '') + '>';

    if (imageCount > 0) {
      images.forEach(function (url, i) {
        html += '<img src="' + esc(url) + '"' +
          ' alt="' + esc(slab.slab_no) + '"' +
          ' loading="lazy"' +
          ' class="slab-card__image' + (i === 0 ? ' is-active' : '') + '"' +
          ' data-slab-image="' + i + '">';
      });

      if (hasGallery) {
        html += '<div class="slab-card__gallery-controls">';
        html += '<button type="button" class="slab-card__gallery-nav" data-slab-gallery-prev aria-label="Previous">' + ICONS.prevArrowSmall + '</button>';
        html += '<span class="slab-card__gallery-counter"><span data-slab-gallery-current>1</span>/' + imageCount + '</span>';
        html += '<button type="button" class="slab-card__gallery-nav" data-slab-gallery-next aria-label="Next">' + ICONS.nextArrowSmall + '</button>';
        html += '</div>';
      }
    } else {
      html += '<div class="slab-card__placeholder">' + ICONS.placeholder + '</div>';
    }

    html += '</div>'; // .slab-card__image-wrapper

    // Content
    html += '<div class="slab-card__content">';
    html += '<h4 class="slab-card__title">' + esc(slab.slab_no) + '</h4>';
    html += '<div class="slab-card__details">';

    if (settings.showDimensions) {
      html += '<div class="slab-card__detail">';
      html += '<span class="slab-card__detail-label">Size</span>';
      html += '<span class="slab-card__detail-value">' +
        esc(slab.length_cm) + ' &times; ' + esc(slab.width_cm) + ' &times; ' + esc(slab.thickness_cm) + ' cm</span>';
      html += '</div>';
    }

    if (settings.showFinish && (slab.finish || bundle.finish)) {
      html += '<div class="slab-card__detail">';
      html += '<span class="slab-card__detail-label">Finish</span>';
      html += '<span class="slab-card__detail-value">' + esc(capitalize(slab.finish || bundle.finish)) + '</span>';
      html += '</div>';
    }

    if (settings.showQuantity) {
      html += '<div class="slab-card__detail">';
      html += '<span class="slab-card__detail-label">Area</span>';
      html += '<span class="slab-card__detail-value">' + fmtArea2(slab.m2) + ' m&sup2;</span>';
      html += '</div>';
    }

    html += '</div>'; // .slab-card__details
    html += '</div>'; // .slab-card__content
    html += '</div>'; // .slab-card

    return html;
  }

  /* ========================================================================
   * INTERACTION HANDLERS
   * ====================================================================== */

  /**
   * Block accordion toggle.
   * (Demo script lines 972-987)
   */
  function initAccordion(section) {
    section.querySelectorAll('[data-block-toggle]').forEach(function (toggle) {
      toggle.addEventListener('click', function () {
        var block = this.closest('[data-block-accordion]');
        var content = block.querySelector('[data-block-content]');
        var isExpanded = this.getAttribute('aria-expanded') === 'true';

        if (isExpanded) {
          content.style.display = 'none';
          this.setAttribute('aria-expanded', 'false');
        } else {
          content.style.display = 'block';
          this.setAttribute('aria-expanded', 'true');
        }
      });
    });
  }

  /**
   * Bundle modal open/close with body scroll lock and Escape key.
   * (Demo script lines 990-1029)
   */
  function initBundleModals(section) {
    // Open
    section.addEventListener('click', function (e) {
      var openBtn = e.target.closest('[data-open-bundle-modal]');
      if (!openBtn) return;

      var modalId = openBtn.getAttribute('data-open-bundle-modal');
      var modal = document.getElementById(modalId);
      if (modal) {
        modal.classList.add('is-open');
        document.body.style.overflow = 'hidden';

        // Lazy-load slabs if needed
        var slabsContainer = modal.querySelector('[data-modal-slabs]');
        if (slabsContainer && !slabsContainer.dataset.loaded) {
          loadSlabsForModal(slabsContainer);
        }
      }
    });

    // Close via close button
    section.addEventListener('click', function (e) {
      var closeBtn = e.target.closest('[data-close-bundle-modal]');
      if (!closeBtn) return;
      var modal = closeBtn.closest('.bundle-modal');
      if (modal) {
        modal.classList.remove('is-open');
        document.body.style.overflow = '';
      }
    });

    // Close via click on backdrop
    section.querySelectorAll('.bundle-modal').forEach(function (modal) {
      modal.addEventListener('click', function (e) {
        if (e.target === this) {
          this.classList.remove('is-open');
          document.body.style.overflow = '';
        }
      });
    });

    // Close via Escape key (handled globally below)
  }

  /**
   * Bundle gallery navigation (prev/next, thumbnails, counter).
   * (Demo script lines 1034-1080)
   */
  function initGalleries(section) {
    section.querySelectorAll('[data-gallery]').forEach(function (gallery) {
      var images = gallery.querySelectorAll('[data-gallery-image]');
      var thumbs = gallery.querySelectorAll('[data-gallery-thumb]');
      var prevBtn = gallery.querySelector('[data-gallery-prev]');
      var nextBtn = gallery.querySelector('[data-gallery-next]');
      var currentEl = gallery.querySelector('[data-gallery-current]');
      var currentIndex = 0;
      var total = images.length;

      if (total <= 1) return;

      function showImage(index) {
        if (index < 0) index = total - 1;
        if (index >= total) index = 0;
        currentIndex = index;

        images.forEach(function (img, i) {
          img.classList.toggle('is-active', i === currentIndex);
        });
        thumbs.forEach(function (thumb, i) {
          thumb.classList.toggle('is-active', i === currentIndex);
        });
        if (currentEl) currentEl.textContent = currentIndex + 1;
      }

      if (prevBtn) {
        prevBtn.addEventListener('click', function (e) {
          e.stopPropagation();
          showImage(currentIndex - 1);
        });
      }
      if (nextBtn) {
        nextBtn.addEventListener('click', function (e) {
          e.stopPropagation();
          showImage(currentIndex + 1);
        });
      }

      thumbs.forEach(function (thumb) {
        thumb.addEventListener('click', function (e) {
          e.stopPropagation();
          var idx = parseInt(this.getAttribute('data-gallery-thumb'), 10);
          showImage(idx);
        });
      });
    });
  }

  /**
   * Slab mini-gallery navigation.
   * (Demo script lines 1082-1116)
   */
  function initSlabGalleries(section) {
    section.querySelectorAll('[data-slab-gallery]').forEach(function (gallery) {
      var images = gallery.querySelectorAll('[data-slab-image]');
      var prevBtn = gallery.querySelector('[data-slab-gallery-prev]');
      var nextBtn = gallery.querySelector('[data-slab-gallery-next]');
      var currentEl = gallery.querySelector('[data-slab-gallery-current]');
      var currentIndex = 0;
      var total = images.length;

      if (total <= 1) return;

      function showImage(index) {
        if (index < 0) index = total - 1;
        if (index >= total) index = 0;
        currentIndex = index;

        images.forEach(function (img, i) {
          img.classList.toggle('is-active', i === currentIndex);
        });
        if (currentEl) currentEl.textContent = currentIndex + 1;
      }

      if (prevBtn) {
        prevBtn.addEventListener('click', function (e) {
          e.stopPropagation();
          showImage(currentIndex - 1);
        });
      }
      if (nextBtn) {
        nextBtn.addEventListener('click', function (e) {
          e.stopPropagation();
          showImage(currentIndex + 1);
        });
      }
    });
  }

  /**
   * Block gallery card navigation.
   * (Demo script lines 1118-1157)
   */
  function initBlockGalleries(section) {
    section.querySelectorAll('[data-block-gallery-card]').forEach(function (card) {
      var gallery = card.querySelector('[data-block-card-gallery]');
      if (!gallery) return;

      var images = gallery.querySelectorAll('[data-block-card-image]');
      var prevBtn = gallery.querySelector('[data-block-card-prev]');
      var nextBtn = gallery.querySelector('[data-block-card-next]');
      var currentEl = gallery.querySelector('[data-block-card-current]');
      var currentIndex = 0;
      var total = images.length;

      if (total <= 1) return;

      function showImage(index) {
        if (index < 0) index = total - 1;
        if (index >= total) index = 0;
        currentIndex = index;

        images.forEach(function (img, i) {
          img.classList.toggle('is-active', i === currentIndex);
        });
        if (currentEl) currentEl.textContent = currentIndex + 1;
      }

      if (prevBtn) {
        prevBtn.addEventListener('click', function (e) {
          e.stopPropagation();
          showImage(currentIndex - 1);
        });
      }
      if (nextBtn) {
        nextBtn.addEventListener('click', function (e) {
          e.stopPropagation();
          showImage(currentIndex + 1);
        });
      }
    });
  }

  /**
   * Video player controls (play overlay, pause/ended events).
   * (Demo script lines 1159-1213)
   */
  function initVideoPlayers(section) {
    // Block gallery card videos
    section.querySelectorAll('[data-block-video-play]').forEach(function (playBtn) {
      var wrapper = playBtn.closest('.block-gallery-card__video-wrapper');
      if (!wrapper) return;

      var video = wrapper.querySelector('video');
      var overlay = wrapper.querySelector('[data-block-video-overlay]');
      if (!video) return;

      playBtn.addEventListener('click', function () {
        video.play();
        if (overlay) overlay.classList.add('is-hidden');
      });
      video.addEventListener('pause', function () {
        if (overlay) overlay.classList.remove('is-hidden');
      });
      video.addEventListener('ended', function () {
        if (overlay) overlay.classList.remove('is-hidden');
      });
      video.addEventListener('play', function () {
        if (overlay) overlay.classList.add('is-hidden');
      });
    });

    // Generic video players
    section.querySelectorAll('[data-video-play]').forEach(function (playBtn) {
      var wrapper = playBtn.closest('.inventory-cards__block-video-wrapper');
      if (!wrapper) return;

      var video = wrapper.querySelector('video');
      var overlay = wrapper.querySelector('[data-video-overlay]');
      if (!video) return;

      playBtn.addEventListener('click', function () {
        video.play();
        if (overlay) overlay.classList.add('is-hidden');
      });
      video.addEventListener('pause', function () {
        if (overlay) overlay.classList.remove('is-hidden');
      });
      video.addEventListener('ended', function () {
        if (overlay) overlay.classList.remove('is-hidden');
      });
      video.addEventListener('play', function () {
        if (overlay) overlay.classList.add('is-hidden');
      });
    });
  }

  /* ========================================================================
   * SELECTION STATE
   * ====================================================================== */

  var selectedItems = new Map();
  var activeBlock = null;
  var activeBlockId = null;
  var pendingSelection = null;

  // Slab cache: bundleId -> slabs array
  var slabCache = new Map();

  // Block detail cache: blockId -> mapped block with bundles
  var blockDetailCache = new Map();

  // Store reference to section and settings for later use
  var _section = null;
  var _settings = null;
  var _stoneName = '';

  // Quote bar DOM references (set during init)
  var quoteBar, quoteCountEl, quoteM2El, quoteButton;
  var quoteModal, quoteForm, quoteItemsList, quoteTotalM2;
  var quoteError, quoteErrorMessage, quoteSuccess, quoteRef, quoteSubmitBtn;

  // Lightbox DOM references (set during init)
  var lightboxEl = null;
  var lightboxImg = null;
  var lightboxCurrentEl = null;
  var lightboxTotalEl = null;
  var lightboxPrevBtn = null;
  var lightboxNextBtn = null;
  var lightboxCounter = null;
  var lightboxImages = [];
  var lightboxIndex = 0;

  /**
   * Create the lightbox DOM element (appended to body once).
   */
  function createLightbox() {
    var el = document.createElement('div');
    el.className = 'lightbox';
    el.setAttribute('role', 'dialog');
    el.setAttribute('aria-modal', 'true');
    el.setAttribute('aria-label', 'Image viewer');

    var backdrop = document.createElement('div');
    backdrop.className = 'lightbox__backdrop';

    var content = document.createElement('div');
    content.className = 'lightbox__content';

    var img = document.createElement('img');
    img.className = 'lightbox__image';
    img.alt = '';
    content.appendChild(img);

    var closeBtn = document.createElement('button');
    closeBtn.className = 'lightbox__close';
    closeBtn.type = 'button';
    closeBtn.setAttribute('aria-label', 'Close');
    closeBtn.innerHTML = ICONS.close;

    var prevBtn = document.createElement('button');
    prevBtn.className = 'lightbox__nav lightbox__nav--prev';
    prevBtn.type = 'button';
    prevBtn.setAttribute('aria-label', 'Previous image');
    prevBtn.innerHTML = ICONS.prevArrow;

    var nextBtn = document.createElement('button');
    nextBtn.className = 'lightbox__nav lightbox__nav--next';
    nextBtn.type = 'button';
    nextBtn.setAttribute('aria-label', 'Next image');
    nextBtn.innerHTML = ICONS.nextArrow;

    var counter = document.createElement('div');
    counter.className = 'lightbox__counter';
    var currentSpan = document.createElement('span');
    currentSpan.className = 'lightbox__current';
    currentSpan.textContent = '1';
    var totalSpan = document.createElement('span');
    totalSpan.className = 'lightbox__total';
    totalSpan.textContent = '1';
    counter.appendChild(currentSpan);
    counter.appendChild(document.createTextNode(' / '));
    counter.appendChild(totalSpan);

    el.appendChild(backdrop);
    el.appendChild(content);
    el.appendChild(closeBtn);
    el.appendChild(prevBtn);
    el.appendChild(nextBtn);
    el.appendChild(counter);

    document.body.appendChild(el);

    // Cache references
    lightboxEl = el;
    lightboxImg = img;
    lightboxCurrentEl = currentSpan;
    lightboxTotalEl = totalSpan;
    lightboxPrevBtn = prevBtn;
    lightboxNextBtn = nextBtn;
    lightboxCounter = counter;

    // Close on backdrop click
    backdrop.addEventListener('click', closeLightbox);

    // Close on close button click
    closeBtn.addEventListener('click', closeLightbox);

    // Nav button clicks
    prevBtn.addEventListener('click', function (e) {
      e.stopPropagation();
      navigateLightboxPrev();
    });
    nextBtn.addEventListener('click', function (e) {
      e.stopPropagation();
      navigateLightboxNext();
    });

    // Touch/swipe support
    var touchStartX = 0;
    el.addEventListener('touchstart', function (e) {
      touchStartX = e.changedTouches[0].screenX;
    }, { passive: true });
    el.addEventListener('touchend', function (e) {
      var delta = e.changedTouches[0].screenX - touchStartX;
      if (delta > 50) {
        navigateLightboxPrev();
      } else if (delta < -50) {
        navigateLightboxNext();
      }
    }, { passive: true });
  }

  /**
   * Resolve the gallery image set from a clicked image element.
   * Returns { images: string[], index: number }
   */
  function resolveGalleryImages(clickedImg) {
    var images = [];
    var index = 0;
    var src = clickedImg.src || clickedImg.getAttribute('src');

    // Block gallery images
    if (clickedImg.hasAttribute('data-block-card-image')) {
      var gallery = clickedImg.closest('[data-block-card-gallery]');
      if (gallery) {
        gallery.querySelectorAll('[data-block-card-image]').forEach(function (img) {
          images.push(img.src);
        });
        index = parseInt(clickedImg.getAttribute('data-block-card-image'), 10) || 0;
      }
    }
    // Bundle modal gallery images
    else if (clickedImg.hasAttribute('data-gallery-image')) {
      var gallery = clickedImg.closest('[data-gallery]');
      if (gallery) {
        gallery.querySelectorAll('[data-gallery-image]').forEach(function (img) {
          images.push(img.src);
        });
        index = parseInt(clickedImg.getAttribute('data-gallery-image'), 10) || 0;
      }
    }
    // Slab card images
    else if (clickedImg.hasAttribute('data-slab-image')) {
      var gallery = clickedImg.closest('[data-slab-gallery]');
      if (gallery) {
        gallery.querySelectorAll('[data-slab-image]').forEach(function (img) {
          images.push(img.src);
        });
        index = parseInt(clickedImg.getAttribute('data-slab-image'), 10) || 0;
      } else {
        images.push(src);
        index = 0;
      }
    }
    // Bundle card thumbnail (single image)
    else if (clickedImg.classList.contains('bundle-card__image')) {
      images.push(src);
      index = 0;
    }

    // Fallback: single image
    if (images.length === 0 && src) {
      images.push(src);
      index = 0;
    }

    return { images: images, index: index };
  }

  /**
   * Open the lightbox with a set of images.
   */
  function openLightbox(images, startIndex) {
    if (!lightboxEl || !images || images.length === 0) return;

    lightboxImages = images;
    lightboxIndex = startIndex || 0;

    showLightboxImage(lightboxIndex);

    // Show/hide nav and counter for multi-image sets
    var multi = images.length > 1;
    lightboxPrevBtn.style.display = multi ? '' : 'none';
    lightboxNextBtn.style.display = multi ? '' : 'none';
    lightboxCounter.style.display = multi ? '' : 'none';

    lightboxEl.classList.add('is-open');
    document.body.style.overflow = 'hidden';
  }

  /**
   * Close the lightbox.
   */
  function closeLightbox() {
    if (!lightboxEl) return;

    lightboxEl.classList.remove('is-open');
    lightboxImages = [];

    // Only restore scroll if no other modal is open
    var hasOpenModal = document.querySelector('.bundle-modal.is-open') ||
      document.querySelector('.quote-modal.is-open') ||
      document.getElementById('block-warning-modal');
    if (!hasOpenModal) {
      document.body.style.overflow = '';
    }
  }

  /**
   * Display the image at the given index in the lightbox.
   */
  function showLightboxImage(index) {
    var total = lightboxImages.length;
    if (total === 0) return;

    if (index < 0) index = total - 1;
    if (index >= total) index = 0;
    lightboxIndex = index;

    lightboxImg.src = lightboxImages[lightboxIndex];
    lightboxImg.alt = 'Image ' + (lightboxIndex + 1) + ' of ' + total;
    lightboxCurrentEl.textContent = lightboxIndex + 1;
    lightboxTotalEl.textContent = total;

    // Handle load failure
    lightboxImg.onerror = function () {
      this.src = 'data:image/svg+xml,' + encodeURIComponent(
        '<svg viewBox="0 0 24 24" fill="none" stroke="%23666" stroke-width="1.5" xmlns="http://www.w3.org/2000/svg">' +
        '<rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>' +
        '<circle cx="8.5" cy="8.5" r="1.5"/>' +
        '<polyline points="21 15 16 10 5 21"/>' +
        '</svg>'
      );
      this.onerror = null;
    };
  }

  function navigateLightboxPrev() {
    if (lightboxImages.length > 1) {
      showLightboxImage(lightboxIndex - 1);
    }
  }

  function navigateLightboxNext() {
    if (lightboxImages.length > 1) {
      showLightboxImage(lightboxIndex + 1);
    }
  }

  /**
   * Register delegated click handler for lightbox on a root element.
   */
  function initLightboxHandlers(root) {
    root.addEventListener('click', function (e) {
      var img = e.target.closest(
        '[data-block-card-image], ' +
        '.bundle-card__image, ' +
        '[data-gallery-image], ' +
        '[data-slab-image]'
      );
      if (!img) return;

      // Skip placeholder elements
      if (img.closest('.bundle-card__placeholder') || img.closest('.slab-card__placeholder')) return;

      var result = resolveGalleryImages(img);
      if (result.images.length > 0) {
        openLightbox(result.images, result.index);
      }
    });
  }

  /**
   * Create the block scope warning modal DOM.
   * (Demo script lines 462-542)
   */
  function createBlockWarningModal(newBlock) {
    var modal = document.createElement('div');
    modal.id = 'block-warning-modal';
    modal.className = 'block-warning-modal';

    var overlay = document.createElement('div');
    overlay.className = 'block-warning-modal__overlay';

    var content = document.createElement('div');
    content.className = 'block-warning-modal__content';

    var iconDiv = document.createElement('div');
    iconDiv.className = 'block-warning-modal__icon';
    var svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('width', '48');
    svg.setAttribute('height', '48');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('stroke', 'currentColor');
    svg.setAttribute('stroke-width', '2');
    var path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', 'M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z');
    var line1 = document.createElementNS('http://www.w3.org/2000/svg', 'line');
    line1.setAttribute('x1', '12'); line1.setAttribute('y1', '9');
    line1.setAttribute('x2', '12'); line1.setAttribute('y2', '13');
    var line2 = document.createElementNS('http://www.w3.org/2000/svg', 'line');
    line2.setAttribute('x1', '12'); line2.setAttribute('y1', '17');
    line2.setAttribute('x2', '12.01'); line2.setAttribute('y2', '17');
    svg.appendChild(path);
    svg.appendChild(line1);
    svg.appendChild(line2);
    iconDiv.appendChild(svg);

    var title = document.createElement('h3');
    title.className = 'block-warning-modal__title';
    title.textContent = 'Different Block Selected';

    var text = document.createElement('p');
    text.className = 'block-warning-modal__text';
    text.textContent = 'You\'re selecting from a different block. Each quote request can only include items from one block.';

    var current = document.createElement('p');
    current.className = 'block-warning-modal__current';
    var currentLabel = document.createTextNode('Current selection: ');
    var currentStrong = document.createElement('strong');
    currentStrong.textContent = activeBlock;
    var br = document.createElement('br');
    var newLabel = document.createTextNode('New selection: ');
    var newStrong = document.createElement('strong');
    newStrong.textContent = newBlock;
    current.appendChild(currentLabel);
    current.appendChild(currentStrong);
    current.appendChild(br);
    current.appendChild(newLabel);
    current.appendChild(newStrong);

    var actions = document.createElement('div');
    actions.className = 'block-warning-modal__actions';
    var cancelBtn = document.createElement('button');
    cancelBtn.type = 'button';
    cancelBtn.className = 'btn btn--secondary';
    cancelBtn.setAttribute('data-block-warning-cancel', '');
    cancelBtn.textContent = 'Keep Current';
    var switchBtn = document.createElement('button');
    switchBtn.type = 'button';
    switchBtn.className = 'btn btn--primary';
    switchBtn.setAttribute('data-block-warning-switch', '');
    switchBtn.textContent = 'Switch Block';
    actions.appendChild(cancelBtn);
    actions.appendChild(switchBtn);

    content.appendChild(iconDiv);
    content.appendChild(title);
    content.appendChild(text);
    content.appendChild(current);
    content.appendChild(actions);
    modal.appendChild(overlay);
    modal.appendChild(content);

    return modal;
  }

  /**
   * Show block scope warning modal.
   * (Demo script lines 544-577)
   */
  function showBlockWarning(newBlock, checkbox) {
    pendingSelection = { newBlock: newBlock, checkbox: checkbox };
    var existingWarning = document.getElementById('block-warning-modal');
    if (existingWarning) existingWarning.remove();

    var warningModal = createBlockWarningModal(newBlock);
    document.body.appendChild(warningModal);
    document.body.style.overflow = 'hidden';

    warningModal.querySelector('[data-block-warning-cancel]').addEventListener('click', function () {
      pendingSelection = null;
      warningModal.remove();
      document.body.style.overflow = '';
    });

    warningModal.querySelector('[data-block-warning-switch]').addEventListener('click', function () {
      // Clear all selections
      selectedItems.clear();
      document.querySelectorAll('[data-select-slab], [data-select-bundle]').forEach(function (cb) {
        cb.checked = false;
      });
      activeBlock = pendingSelection.newBlock;
      // Derive block ID from checkbox
      var cb = pendingSelection.checkbox;
      if (cb.hasAttribute('data-select-slab')) {
        activeBlockId = cb.dataset.slabBlockId || null;
      } else {
        activeBlockId = cb.dataset.bundleBlockId || null;
      }
      pendingSelection.checkbox.checked = true;
      handleSelectionChange(pendingSelection.checkbox);
      pendingSelection = null;
      warningModal.remove();
      document.body.style.overflow = '';
    });

    warningModal.querySelector('.block-warning-modal__overlay').addEventListener('click', function () {
      pendingSelection = null;
      warningModal.remove();
      document.body.style.overflow = '';
    });
  }

  /**
   * Update the sticky footer inside a bundle modal.
   * (Demo script lines 580-613)
   */
  function updateModalFooter(modal) {
    if (!modal) return;
    var footer = modal.querySelector('[data-modal-footer]');
    if (!footer) return;

    var count = 0;
    var totalM2 = 0;

    modal.querySelectorAll('[data-select-slab]:checked').forEach(function (cb) {
      count++;
      totalM2 += parseFloat(cb.dataset.slabM2) || 0;
    });

    var bundleCb = modal.querySelector('[data-select-bundle]:checked');
    if (bundleCb) {
      count = parseInt(bundleCb.dataset.bundleSlabCount) || 0;
      totalM2 = parseFloat(bundleCb.dataset.bundleTotalM2) || 0;
    }

    var countEl = footer.querySelector('[data-modal-selected-count]');
    var m2El = footer.querySelector('[data-modal-selected-m2]');
    if (countEl) countEl.textContent = count;
    if (m2El) m2El.textContent = totalM2.toFixed(1);

    if (count > 0) {
      footer.classList.add('is-visible');
    } else {
      footer.classList.remove('is-visible');
    }
  }

  /**
   * Update all modal footers in the section.
   */
  function updateAllModalFooters() {
    if (!_section) return;
    _section.querySelectorAll('.bundle-modal').forEach(function (modal) {
      updateModalFooter(modal);
    });
  }

  /**
   * Handle selection change on slab/bundle checkbox.
   * (Demo script lines 616-669)
   */
  function handleSelectionChange(checkbox) {
    var isSlab = checkbox.hasAttribute('data-select-slab');
    var code = isSlab ? checkbox.dataset.slabCode : checkbox.dataset.bundleCode;
    var block = isSlab ? checkbox.dataset.slabBlock : checkbox.dataset.bundleBlock;
    var modal = checkbox.closest('.bundle-modal');

    if (checkbox.checked) {
      if (isSlab) {
        // Deselect bundle in same modal (mutual exclusivity)
        if (modal) {
          var bundleCheckbox = modal.querySelector('[data-select-bundle]');
          if (bundleCheckbox && bundleCheckbox.checked) {
            bundleCheckbox.checked = false;
            selectedItems.delete(bundleCheckbox.dataset.bundleCode);
          }
        }
        selectedItems.set(code, {
          id:        checkbox.dataset.slabId || '',
          type:      'slab',
          code:      code,
          product:   checkbox.dataset.slabProduct,
          block:     block,
          blockId:   checkbox.dataset.slabBlockId || '',
          length:    checkbox.dataset.slabLength,
          width:     checkbox.dataset.slabWidth,
          thickness: checkbox.dataset.slabThickness,
          m2:        checkbox.dataset.slabM2,
          finish:    checkbox.dataset.slabFinish,
          price:     checkbox.dataset.slabPrice
        });
      } else {
        // Deselect all individual slabs in same modal (mutual exclusivity)
        if (modal) {
          modal.querySelectorAll('[data-select-slab]').forEach(function (slabCb) {
            slabCb.checked = false;
            selectedItems.delete(slabCb.dataset.slabCode);
          });
        }
        selectedItems.set(code, {
          id:        checkbox.dataset.bundleId || '',
          type:      'bundle',
          code:      code,
          product:   checkbox.dataset.bundleProduct,
          block:     block,
          blockId:   checkbox.dataset.bundleBlockId || '',
          slabCount: checkbox.dataset.bundleSlabCount,
          m2:        checkbox.dataset.bundleTotalM2,
          finish:    checkbox.dataset.bundleFinish || '',
          price:     checkbox.dataset.bundlePrice
        });
      }
    } else {
      selectedItems.delete(code);
      if (selectedItems.size === 0) {
        activeBlock = null;
        activeBlockId = null;
      }
    }

    updateQuoteBar();
    updateModalFooter(modal);
  }

  /**
   * Update the sticky quote request bar.
   * (Demo script lines 671-692)
   */
  function updateQuoteBar() {
    var count = selectedItems.size;
    var totalM2 = 0;
    selectedItems.forEach(function (item) {
      totalM2 += parseFloat(item.m2) || 0;
    });

    if (quoteCountEl) quoteCountEl.textContent = count;
    if (quoteM2El) quoteM2El.textContent = totalM2.toFixed(1) + ' m\u00B2';

    if (quoteButton) {
      quoteButton.disabled = count === 0;
    }

    if (quoteBar) {
      if (count > 0) {
        quoteBar.classList.add('is-visible');
      } else {
        quoteBar.classList.remove('is-visible');
      }
    }
  }

  /**
   * Init slab and bundle selection checkbox handlers.
   * Uses event delegation on the section.
   * (Demo script lines 795-836)
   */
  function initSelectionHandlers(section) {
    section.addEventListener('change', function (e) {
      var checkbox = e.target;

      // Slab selection
      if (checkbox.hasAttribute('data-select-slab')) {
        var slabBlock = checkbox.dataset.slabBlock;

        // Block scope check
        if (checkbox.checked && activeBlock && slabBlock && activeBlock !== slabBlock) {
          e.preventDefault();
          checkbox.checked = false;
          showBlockWarning(slabBlock, checkbox);
          return;
        }

        // Set active block on first selection
        if (checkbox.checked && !activeBlock && slabBlock) {
          activeBlock = slabBlock;
          activeBlockId = checkbox.dataset.slabBlockId || null;
        }

        handleSelectionChange(checkbox);
        return;
      }

      // Bundle selection
      if (checkbox.hasAttribute('data-select-bundle')) {
        var bundleBlock = checkbox.dataset.bundleBlock;

        // Block scope check
        if (checkbox.checked && activeBlock && bundleBlock && activeBlock !== bundleBlock) {
          e.preventDefault();
          checkbox.checked = false;
          showBlockWarning(bundleBlock, checkbox);
          return;
        }

        // Set active block on first selection
        if (checkbox.checked && !activeBlock && bundleBlock) {
          activeBlock = bundleBlock;
          activeBlockId = checkbox.dataset.bundleBlockId || null;
        }

        handleSelectionChange(checkbox);
      }
    });
  }

  /**
   * Init "Add to Quote" button inside modal footers.
   * (Demo script lines 838-847)
   */
  function initModalAddToQuote(section) {
    section.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-modal-add-to-quote]');
      if (!btn) return;
      var modal = btn.closest('.bundle-modal');
      if (modal) {
        modal.classList.remove('is-open');
        document.body.style.overflow = '';
      }
    });
  }

  /* ========================================================================
   * QUOTE SUBMISSION
   * ====================================================================== */

  /**
   * Create a remove button for a quote item.
   * (Demo script lines 694-716)
   */
  function createRemoveButton(code) {
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'quote-modal__item-remove';
    btn.setAttribute('data-remove-item', code);
    var svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('width', '16');
    svg.setAttribute('height', '16');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('stroke', 'currentColor');
    svg.setAttribute('stroke-width', '2');
    var line1 = document.createElementNS('http://www.w3.org/2000/svg', 'line');
    line1.setAttribute('x1', '18'); line1.setAttribute('y1', '6');
    line1.setAttribute('x2', '6'); line1.setAttribute('y2', '18');
    var line2 = document.createElementNS('http://www.w3.org/2000/svg', 'line');
    line2.setAttribute('x1', '6'); line2.setAttribute('y1', '6');
    line2.setAttribute('x2', '18'); line2.setAttribute('y2', '18');
    svg.appendChild(line1);
    svg.appendChild(line2);
    btn.appendChild(svg);
    return btn;
  }

  /**
   * Create a quote item DOM element.
   * (Demo script lines 718-756)
   */
  function createQuoteItem(item, code) {
    var itemEl = document.createElement('div');
    itemEl.className = 'quote-modal__item';

    var infoDiv = document.createElement('div');
    infoDiv.className = 'quote-modal__item-info';
    var strong = document.createElement('strong');
    strong.textContent = (item.type === 'bundle' ? '' : 'Slab ') + code;
    var productSpan = document.createElement('span');
    productSpan.textContent = item.product;
    infoDiv.appendChild(strong);
    infoDiv.appendChild(productSpan);

    var detailsDiv = document.createElement('div');
    detailsDiv.className = 'quote-modal__item-details';
    var dimSpan = document.createElement('span');
    if (item.type === 'slab') {
      dimSpan.textContent = item.length + '\u00D7' + item.width + '\u00D7' + item.thickness + 'cm';
    } else {
      dimSpan.textContent = item.slabCount + ' slabs';
    }
    var m2Span = document.createElement('span');
    m2Span.textContent = parseFloat(item.m2).toFixed(2) + ' m\u00B2';
    detailsDiv.appendChild(dimSpan);
    detailsDiv.appendChild(m2Span);
    if (item.price && parseFloat(item.price) > 0) {
      var priceSpan = document.createElement('span');
      priceSpan.textContent = '\u00A3' + Math.round(parseFloat(item.price));
      detailsDiv.appendChild(priceSpan);
    }

    var removeBtn = createRemoveButton(code);

    itemEl.appendChild(infoDiv);
    itemEl.appendChild(detailsDiv);
    itemEl.appendChild(removeBtn);

    return itemEl;
  }

  /**
   * Render the list of selected items inside the quote modal.
   * (Demo script lines 758-792)
   */
  function renderQuoteItems() {
    if (!quoteItemsList) return;

    while (quoteItemsList.firstChild) {
      quoteItemsList.removeChild(quoteItemsList.firstChild);
    }

    var totalM2 = 0;

    selectedItems.forEach(function (item, code) {
      totalM2 += parseFloat(item.m2) || 0;
      var itemEl = createQuoteItem(item, code);
      quoteItemsList.appendChild(itemEl);
    });

    if (quoteTotalM2) quoteTotalM2.textContent = totalM2.toFixed(2);

    // Bind remove buttons
    quoteItemsList.querySelectorAll('[data-remove-item]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var itemCode = this.getAttribute('data-remove-item');
        selectedItems.delete(itemCode);
        var checkbox = document.querySelector(
          '[data-select-slab][data-slab-code="' + itemCode + '"], ' +
          '[data-select-bundle][data-bundle-code="' + itemCode + '"]'
        );
        if (checkbox) checkbox.checked = false;
        if (selectedItems.size === 0) {
          activeBlock = null;
          activeBlockId = null;
        }
        updateQuoteBar();
        updateAllModalFooters();
        renderQuoteItems();
        if (selectedItems.size === 0) {
          closeQuoteModal();
        }
      });
    });
  }

  /**
   * Open the quote request modal.
   * (Demo script lines 850-859)
   */
  function openQuoteModal() {
    if (quoteModal) {
      renderQuoteItems();
      quoteModal.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      if (quoteForm) quoteForm.style.display = '';
      if (quoteSuccess) quoteSuccess.style.display = 'none';
      if (quoteError) quoteError.style.display = 'none';
    }
  }

  /**
   * Close the quote request modal.
   * (Demo script lines 861-866)
   */
  function closeQuoteModal() {
    if (quoteModal) {
      quoteModal.classList.remove('is-open');
      document.body.style.overflow = '';
    }
  }

  /**
   * Build the quote API payload from selectedItems + form data.
   * Adapted from demo script lines 882-926 to use FindMyMarbleAPI format.
   */
  function buildQuotePayload(formData) {
    var items = [];
    var totalM2 = 0;
    selectedItems.forEach(function (item) {
      var m2 = parseFloat(item.m2) || 0;
      totalM2 += m2;
      var entry = {
        id:       item.id,
        type:     item.type,
        code:     item.code,
        block_id: item.blockId || '',
        m2:       m2,
        finish:   item.finish || null,
        price:    parseFloat(item.price) || 0
      };
      if (item.type === 'slab' && item.length && item.width && item.thickness) {
        entry.dimensions = item.length + '\u00D7' + item.width + '\u00D7' + item.thickness + 'mm';
      }
      if (item.type === 'bundle' && item.slabCount) {
        entry.slab_count = parseInt(item.slabCount, 10) || 0;
      }
      items.push(entry);
    });

    return {
      stone_name:   _stoneName,
      block_id:     activeBlockId || '',
      block_code:   activeBlock || '',
      quantity:     Math.round(totalM2 * 100) / 100,
      unit:         'sqm',
      items:        items,
      project_type: formData.get('project_type') || undefined,
      customer: {
        contact_name:  formData.get('name'),
        company_name:  formData.get('company') || formData.get('name'),
        email:         formData.get('email'),
        phone:         formData.get('phone') || undefined,
        customer_type: mapProjectType(formData.get('project_type')),
        notes:         formData.get('message') || undefined
      }
    };
  }

  /**
   * Init the quote form: open/close, submission.
   * (Demo script lines 850-970)
   */
  function initQuoteForm() {
    // Cache DOM refs
    quoteBar          = document.querySelector('[data-quote-bar]');
    quoteCountEl      = document.querySelector('[data-quote-count]');
    quoteM2El         = document.querySelector('[data-quote-m2]');
    quoteButton       = document.querySelector('[data-open-quote-form]');
    quoteModal        = document.querySelector('[data-quote-modal]');
    quoteForm         = document.querySelector('[data-quote-form]');
    quoteItemsList    = document.querySelector('[data-quote-items-list]');
    quoteTotalM2      = document.querySelector('[data-quote-total-m2]');
    quoteError        = document.querySelector('[data-quote-error]');
    quoteErrorMessage = document.querySelector('[data-quote-error-message]');
    quoteSuccess      = document.querySelector('[data-quote-success]');
    quoteRef          = document.querySelector('[data-quote-ref]');
    quoteSubmitBtn    = document.querySelector('[data-quote-submit]');

    // Open
    if (quoteButton) {
      quoteButton.addEventListener('click', openQuoteModal);
    }

    // Close
    document.querySelectorAll('[data-close-quote-modal]').forEach(function (btn) {
      btn.addEventListener('click', closeQuoteModal);
    });

    if (quoteModal) {
      var overlay = quoteModal.querySelector('.quote-modal__overlay');
      if (overlay) overlay.addEventListener('click', closeQuoteModal);
    }

    // Form submission
    if (quoteForm) {
      quoteForm.addEventListener('submit', function (e) {
        e.preventDefault();

        if (quoteError) quoteError.style.display = 'none';

        var formData = new FormData(quoteForm);
        var payload = buildQuotePayload(formData);

        if (quoteSubmitBtn) {
          quoteSubmitBtn.disabled = true;
          var submitText = quoteSubmitBtn.querySelector('.quote-modal__submit-text');
          var submitLoading = quoteSubmitBtn.querySelector('.quote-modal__submit-loading');
          if (submitText) submitText.style.display = 'none';
          if (submitLoading) submitLoading.style.display = 'inline-flex';
        }

        // Submit via FindMyMarbleAPI
        var api = window.FindMyMarbleAPI;
        if (!api) {
          showQuoteError('API not available. Please try again later.');
          resetSubmitButton();
          return;
        }

        api.submitQuote(payload).then(function (result) {
          if (result && result.error) {
            showQuoteError(result.message || 'Failed to submit quote.');
            resetSubmitButton();
            return;
          }

          // Success
          if (quoteForm) quoteForm.style.display = 'none';
          if (quoteRef) quoteRef.textContent = result.reference_number || '';
          if (quoteSuccess) quoteSuccess.style.display = 'flex';

          // Reset selection state
          selectedItems.clear();
          activeBlock = null;
          activeBlockId = null;
          document.querySelectorAll('[data-select-slab], [data-select-bundle]').forEach(function (cb) {
            cb.checked = false;
          });
          updateQuoteBar();
          updateAllModalFooters();
          resetSubmitButton();
        }).catch(function (err) {
          showQuoteError(err.message || 'An unexpected error occurred.');
          resetSubmitButton();
        });
      });
    }

    // Initial state
    updateQuoteBar();
  }

  function showQuoteError(message) {
    if (quoteError) {
      quoteError.style.display = 'flex';
      if (quoteErrorMessage) quoteErrorMessage.textContent = message;
    }
  }

  function resetSubmitButton() {
    if (quoteSubmitBtn) {
      quoteSubmitBtn.disabled = false;
      var submitText = quoteSubmitBtn.querySelector('.quote-modal__submit-text');
      var submitLoading = quoteSubmitBtn.querySelector('.quote-modal__submit-loading');
      if (submitText) submitText.style.display = '';
      if (submitLoading) submitLoading.style.display = 'none';
    }
  }

  /**
   * Escape key handler for all modals.
   * (Demo script lines 1020-1029)
   */
  function initEscapeHandler(section) {
    document.addEventListener('keydown', function (e) {
      // Lightbox keyboard navigation (highest priority)
      if (lightboxEl && lightboxEl.classList.contains('is-open')) {
        if (e.key === 'Escape') {
          closeLightbox();
          return;
        }
        if (e.key === 'ArrowLeft') {
          navigateLightboxPrev();
          return;
        }
        if (e.key === 'ArrowRight') {
          navigateLightboxNext();
          return;
        }
      }

      if (e.key === 'Escape') {
        // Close block warning modal
        var blockWarning = document.getElementById('block-warning-modal');
        if (blockWarning) {
          pendingSelection = null;
          blockWarning.remove();
          document.body.style.overflow = '';
          return;
        }

        // Close open bundle modal
        var openBundleModal = section.querySelector('.bundle-modal.is-open');
        if (openBundleModal) {
          openBundleModal.classList.remove('is-open');
          document.body.style.overflow = '';
          return;
        }

        // Close quote modal
        closeQuoteModal();
      }
    });
  }

  /* ========================================================================
   * LAZY SLAB LOADING
   * ====================================================================== */

  /**
   * Load slabs for a bundle modal on first open.
   * The container has data-modal-slabs="bundleId".
   */
  function loadSlabsForModal(container) {
    var bundleId = container.getAttribute('data-modal-slabs');
    if (!bundleId) return;

    // Mark as loading to prevent duplicate fetches
    container.dataset.loaded = 'loading';

    var api = window.FindMyMarbleAPI;
    if (!api) {
      container.innerHTML = '<p class="inventory-cards__error-text">API not available.</p>';
      return;
    }

    // Find the block and bundle data for this modal
    var modal = container.closest('.bundle-modal');
    var modalId = modal ? modal.id : '';

    // Look up bundle info from our cache
    var bundleData = null;
    var blockData = null;
    blockDetailCache.forEach(function (block) {
      if (blockData) return; // already found
      (block.bundles || []).forEach(function (b) {
        if (b.id === bundleId) {
          bundleData = b;
          blockData = block;
        }
      });
    });

    api.getBundleSlabs(bundleId).then(function (result) {
      if (result && result.error) {
        container.innerHTML = '<p class="inventory-cards__error-text">Unable to load slabs.</p>';
        container.dataset.loaded = 'error';
        return;
      }

      var apiSlabs = (result && result.slabs) || [];
      var finish = bundleData ? bundleData.finish : '';
      var blockCode = blockData ? blockData.block_code : '';
      var blockId = blockData ? blockData.id : '';

      // Map and filter available slabs
      var slabs = apiSlabs
        .filter(function (s) { return s.status === 'available'; })
        .map(function (s) { return mapSlab(s, finish, blockCode, blockId); });

      // Cache
      slabCache.set(bundleId, slabs);

      // Render
      if (slabs.length === 0) {
        container.innerHTML = '<p class="inventory-cards__empty-text">No slabs currently available.</p>';
      } else {
        var html = '';
        slabs.forEach(function (slab) {
          html += renderSlabCard(slab, bundleData || {}, blockData || {}, _settings);
        });
        container.innerHTML = html;
      }

      container.dataset.loaded = 'true';

      // Init slab galleries for newly rendered content
      initSlabGalleries(container);

    }).catch(function () {
      container.innerHTML = '<p class="inventory-cards__error-text">Unable to load slabs.</p>';
      container.dataset.loaded = 'error';
    });
  }

  /* ========================================================================
   * INITIALIZATION
   * ====================================================================== */

  /**
   * Parse section settings from data attributes.
   */
  function parseSettings(section) {
    return {
      columns:         section.dataset.columns || '3',
      imageRatio:      section.dataset.imageRatio || 'landscape',
      showDimensions:  section.dataset.showDimensions === 'true',
      showFinish:      section.dataset.showFinish === 'true',
      showQuantity:    section.dataset.showQuantity === 'true',
      showPrice:       section.dataset.showPrice === 'true',
      showBundleBuy:   section.dataset.showBundleBuy === 'true',
      showBlockInfo:   section.dataset.showBlockInfo === 'true',
      showInquireBtn:  section.dataset.showInquireButton === 'true',
      headingSize:     section.dataset.headingSize || 'h2'
    };
  }

  /**
   * Show / hide state containers.
   */
  function showState(section, state) {
    var loadingEl = section.querySelector(SELECTORS.loading);
    var contentEl = section.querySelector(SELECTORS.content);
    var errorEl   = section.querySelector(SELECTORS.error);
    var emptyEl   = section.querySelector(SELECTORS.empty);

    if (loadingEl) loadingEl.toggleAttribute('hidden', state !== 'loading');
    if (contentEl) contentEl.toggleAttribute('hidden', state !== 'content');
    if (errorEl)   errorEl.toggleAttribute('hidden', state !== 'error');
    if (emptyEl)   emptyEl.toggleAttribute('hidden', state !== 'empty');
  }

  /**
   * Main loading flow.
   */
  async function loadData(section, settings, stoneName) {
    var api;

    try {
      showState(section, 'loading');
      api = await waitForAPI(5000);
    } catch (err) {
      console.warn('inventory-cards: ' + err.message);
      showState(section, 'error');
      return;
    }

    try {
      // Step 1: Fetch block list
      var blocksResult = await api.getBlocks(stoneName);
      if (blocksResult && blocksResult.error) {
        console.warn('inventory-cards: blocks API error - ' + blocksResult.message);
        showState(section, 'error');
        return;
      }

      var apiBlocks = (blocksResult && blocksResult.blocks) || [];
      if (apiBlocks.length === 0) {
        showState(section, 'empty');
        return;
      }

      // Step 2: Map blocks and render accordion headers immediately
      var blocks = apiBlocks.map(function (b) { return mapBlock(b); });
      var contentEl = section.querySelector(SELECTORS.content);

      var accordionHtml = renderSummaryBar(blocks);
      accordionHtml += '<div class="inventory-blocks">';
      blocks.forEach(function (block, i) {
        accordionHtml += renderBlockAccordion(block, i, settings);
      });
      accordionHtml += '</div>';
      contentEl.innerHTML = accordionHtml;

      // Show content (hide loading)
      showState(section, 'content');

      // Init accordion immediately so users can interact
      initAccordion(section);
      initBlockGalleries(section);
      initVideoPlayers(section);

      // Step 3: Fetch block details in parallel
      var detailPromises = blocks.map(function (block) {
        return api.getBlockDetail(block.id).then(function (result) {
          if (result && result.error) return null;
          return (result && result.block) ? result.block : null;
        }).catch(function () {
          return null;
        });
      });

      var detailResults = await Promise.all(detailPromises);

      // Step 4: For each block detail, render bundle cards + modals
      var modalsHtml = '';
      var modalCounter = 0;

      detailResults.forEach(function (apiBlock, index) {
        if (!apiBlock) return;

        var block = blocks[index];
        var bundles = (apiBlock.bundles || []).map(function (b) {
          return mapBundle(b, block.block_code, block.id);
        });

        // Update block with bundles
        block.bundles = bundles;

        // Cache the block detail
        blockDetailCache.set(block.id, block);

        // Render bundle cards into the grid placeholder
        var placeholder = contentEl.querySelector('[data-bundle-placeholder="' + block.id + '"]');
        if (placeholder) {
          var bundleCardsHtml = '';
          bundles.forEach(function (bundle) {
            modalCounter++;
            var modalId = 'bundle-modal-live-' + modalCounter;
            bundleCardsHtml += renderBundleCard(bundle, settings, modalId);
            modalsHtml += renderBundleModal(bundle, block, settings, modalId);
          });

          if (bundles.length === 0) {
            bundleCardsHtml = '<p class="inventory-cards__no-bundles">No bundles available for this block.</p>';
          }

          placeholder.outerHTML = bundleCardsHtml;
        }

        // Update accordion header stats now that we have bundle count
        var accordionEl = contentEl.querySelector('[data-block-id="' + block.id + '"] .inventory-block__stats');
        if (accordionEl) {
          var bundleCount = bundles.length;
          accordionEl.innerHTML = bundleCount + ' bundle' + (bundleCount !== 1 ? 's' : '') +
            ' &bull; ' + fmtArea1(block.available_sqm) + ' m&sup2;';
        }
      });

      // Append all modals to the section (outside content flow for proper z-index)
      if (modalsHtml) {
        var modalsContainer = document.createElement('div');
        modalsContainer.className = 'inventory-cards__modals';
        modalsContainer.innerHTML = modalsHtml;
        section.appendChild(modalsContainer);
        initLightboxHandlers(modalsContainer);
      }

      // Update summary bar with actual bundle counts
      var summaryBar = contentEl.querySelector('.inventory-cards__total-summary');
      if (summaryBar) {
        var totalBundles = 0;
        var totalM2 = 0;
        blocks.forEach(function (b) {
          totalBundles += (b.bundles && b.bundles.length) || 0;
          totalM2 += b.available_sqm || 0;
        });
        summaryBar.innerHTML =
          '<span class="inventory-cards__stat"><strong>' + blocks.length + '</strong> block' + (blocks.length !== 1 ? 's' : '') + ' available</span>' +
          '<span class="inventory-cards__stat-divider"></span>' +
          '<span class="inventory-cards__stat"><strong>' + totalBundles + '</strong> bundle' + (totalBundles !== 1 ? 's' : '') + ' total</span>' +
          '<span class="inventory-cards__stat-divider"></span>' +
          '<span class="inventory-cards__stat"><strong>' + fmtArea1(totalM2) + '</strong> m&sup2; total</span>';
      }

      // Step 5: Init all interaction handlers
      initBundleModals(section);
      initGalleries(section);
      initLightboxHandlers(section);
      initSelectionHandlers(section);
      initModalAddToQuote(section);
      initEscapeHandler(section);
      initQuoteForm();

      // Deep-link: scroll to block specified in URL hash (e.g. from chatbot card)
      scrollToHashBlock(section);

    } catch (err) {
      console.warn('inventory-cards: unexpected error - ' + (err.message || err));
      showState(section, 'error');
    }
  }

  /**
   * If location.hash matches a block ID, expands its accordion, scrolls to it,
   * and briefly highlights it. Called after the render pipeline completes.
   * @param {Element} section - The inventory section root element
   */
  function scrollToHashBlock(section) {
    try {
      var hash = window.location.hash;
      if (!hash || hash.indexOf('#block-') !== 0) return;

      var blockId = hash.slice('#block-'.length);
      if (!blockId) return;

      var blockEl = section.querySelector('[data-block-id="' + blockId + '"]');
      if (!blockEl) return;

      // Expand accordion if collapsed
      var toggleBtn = blockEl.querySelector('[data-block-toggle]');
      var contentEl = blockEl.querySelector('[data-block-content]');
      if (toggleBtn && toggleBtn.getAttribute('aria-expanded') === 'false') {
        toggleBtn.setAttribute('aria-expanded', 'true');
        if (contentEl) contentEl.style.display = 'block';
      }

      // Scroll (scroll-margin-top in CSS handles sticky header offset)
      blockEl.scrollIntoView({ behavior: 'smooth', block: 'start' });

      // Highlight: remove first to allow animation restart on re-trigger
      blockEl.classList.remove('inventory-block--highlight');
      void blockEl.offsetWidth; // force reflow
      blockEl.classList.add('inventory-block--highlight');

      // Accessibility: focus block for screen readers
      if (!blockEl.hasAttribute('tabindex')) {
        blockEl.setAttribute('tabindex', '-1');
      }
      blockEl.focus({ preventScroll: true });

      // Remove highlight class after animation completes
      setTimeout(function () {
        blockEl.classList.remove('inventory-block--highlight');
      }, 2100);

    } catch (e) {
      // Silent no-op — never break the page
    }
  }

  /**
   * Entry point.
   */
  function init() {
    var section = document.querySelector(SELECTORS.section);
    if (!section) return;

    // Skip if demo mode
    if (section.dataset.demoMode === 'true') return;

    var stoneName = section.dataset.stoneName;
    if (!stoneName) {
      showState(section, 'empty');
      return;
    }

    // Store globals
    _section = section;
    _settings = parseSettings(section);
    _stoneName = stoneName;

    // Create lightbox DOM
    createLightbox();

    // Retry button
    var retryBtn = section.querySelector(SELECTORS.retry);
    if (retryBtn) {
      retryBtn.addEventListener('click', function () {
        loadData(section, _settings, stoneName);
      });
    }

    // Load data
    loadData(section, _settings, stoneName);

    // Re-trigger deep-link scroll if hash changes after page load
    window.addEventListener('hashchange', function () {
      scrollToHashBlock(section);
    });
  }

  /* ========================================================================
   * BOOTSTRAP
   * ====================================================================== */

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
